import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';

export const toggleVariants = cva(
  [
    'relative inline-flex shrink-0 items-center rounded-full bg-gray-200 transition-colors duration-150',
    "after:absolute after:left-0.5 after:rounded-full after:bg-white after:shadow after:transition-transform after:duration-150 after:content-['']",
    'peer-checked:bg-brand',
    'peer-focus-visible:ring-2 peer-focus-visible:ring-brand/30 peer-focus-visible:ring-offset-2',
    'peer-disabled:opacity-40',
  ],
  {
    variants: {
      size: {
        sm: 'h-5 w-9 after:h-4 after:w-4 peer-checked:after:translate-x-4',
        md: 'h-6 w-11 after:h-5 after:w-5 peer-checked:after:translate-x-5',
      },
      error: {
        true: 'ring-2 ring-red-300 peer-focus-visible:ring-red-200',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

type KoobInputToggleProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className' | 'size' | 'type' | 'onChange'
> &
  VariantProps<typeof toggleVariants> & {
    onCheckedChange?: (checked: boolean) => void;
    /** Styled text label; pass children instead for custom label markup */
    label?: React.ReactNode;
    /** @deprecated use data-testid — kept for selectors that expect the `-enabled`/`-disabled` suffix */
    testId?: string;
    /** Not part of the API (see packages/ui/README.md) — stripped at runtime */
    className?: never;
  };

const KoobInputToggle = React.forwardRef<
  HTMLInputElement,
  KoobInputToggleProps
>(
  (
    {
      checked,
      onCheckedChange,
      label,
      children,
      size,
      error,
      disabled,
      testId,
      // className is not part of the API (see packages/ui/README.md) but is
      // still stripped here so a stray one can't clobber the computed classes
      className,
      ...props
    },
    ref,
  ) => (
    <label
      className={cn(
        'inline-flex items-center gap-3',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      )}
      data-testid={
        testId ? `${testId}-${checked ? 'enabled' : 'disabled'}` : undefined
      }
    >
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        className="peer sr-only"
        checked={checked}
        onChange={(event) => onCheckedChange?.(event.target.checked)}
        disabled={disabled}
        aria-invalid={error || undefined}
        {...props}
      />
      <span aria-hidden className={toggleVariants({ size, error })} />
      {children ??
        (label != null && (
          <span className="text-sm font-semibold text-gray-600 select-none">
            {label}
          </span>
        ))}
    </label>
  ),
);

KoobInputToggle.displayName = 'KoobInputToggle';

export default KoobInputToggle;
